import React, { useEffect, useState } from "react";
import { MessageCircle, RefreshCw, Bug, Lightbulb, Heart, HelpCircle, Check, EyeOff, Loader2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

const KIND_META = {
  suggestion: { icon: Lightbulb, color: "#ff4500", label: "Suggestion" },
  bug: { icon: Bug, color: "#dc2626", label: "Bug" },
  praise: { icon: Heart, color: "#059669", label: "Praise" },
  other: { icon: HelpCircle, color: "#666", label: "Other" },
};

export default function FeedbackInboxCard() {
  const [data, setData] = useState({ items: [], counts: { open: 0, actioned: 0, dismissed: 0, total: 0 } });
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("open");
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/feedback?status=${statusFilter}&limit=200`);
      setData(data);
    } catch { toast.error("Could not load feedback"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [statusFilter]);

  const mark = async (id, status) => {
    setBusyId(id);
    try {
      await api.post(`/admin/feedback/${id}/mark`, { status });
      toast.success(`Marked ${status}`);
      await load();
    } catch { toast.error("Could not update"); }
    finally { setBusyId(null); }
  };

  const counts = data.counts || { open: 0, actioned: 0, dismissed: 0, total: 0 };

  return (
    <section className="border hairline rounded-2xl p-5 sm:p-6 mb-6 bg-white" data-testid="admin-feedback-card">
      <header className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div>
          <div className="eyebrow text-[#ff4500] flex items-center gap-1.5 mb-1">
            <MessageCircle className="w-3 h-3" /> User feedback inbox
          </div>
          <h2 className="font-display text-xl sm:text-2xl tracking-tight">What campers are saying</h2>
          <p className="text-xs text-[#666] mt-1">
            Everything users submit via the &ldquo;Feedback&rdquo; button.{" "}
            <strong>{counts.open}</strong> open · {counts.actioned} actioned · {counts.dismissed} dismissed (total: {counts.total})
          </p>
        </div>
        <button
          onClick={load} disabled={loading}
          className="text-xs text-[#666] hover:text-[#ff4500] inline-flex items-center gap-1 disabled:opacity-50"
          data-testid="feedback-inbox-refresh"
        >
          <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </header>

      <div className="flex gap-2 mb-4 flex-wrap">
        {[["open", `Open (${counts.open})`], ["actioned", `Actioned (${counts.actioned})`], ["dismissed", `Dismissed (${counts.dismissed})`], ["all", `All (${counts.total})`]].map(([k, label]) => (
          <button key={k} onClick={() => setStatusFilter(k)}
            className={`px-3 py-1.5 rounded-full text-xs mono uppercase tracking-wider transition-colors ${statusFilter === k ? "bg-[#0e0e0e] text-white" : "bg-[#f5f5f5] text-[#333] hover:bg-[#fff4ee]"}`}
            data-testid={`feedback-filter-${k}`}
          >{label}</button>
        ))}
      </div>

      {loading && data.items.length === 0 && (
        <div className="text-sm text-[#666] flex items-center gap-2 py-8"><Loader2 className="w-3 h-3 animate-spin" /> Loading…</div>
      )}
      {!loading && data.items.length === 0 && (
        <div className="text-center py-10 text-sm text-[#888] border-2 border-dashed hairline rounded-xl bg-[#fafafa]" data-testid="feedback-empty">
          Nothing here — quiet is good news (or the button just went live).
        </div>
      )}

      <ul className="space-y-3" data-testid="feedback-list">
        {data.items.map((f) => {
          const meta = KIND_META[f.kind] || KIND_META.other;
          const Icon = meta.icon;
          return (
            <li key={f.feedback_id} className="border hairline rounded-xl p-4 bg-[#fafafa]" data-testid={`feedback-item-${f.feedback_id}`}>
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] mono uppercase tracking-wider font-bold text-white" style={{ background: meta.color }}>
                    <Icon className="w-3 h-3" strokeWidth={2.5} /> {meta.label}
                  </span>
                  {f.subject && <span className="text-sm font-bold text-[#0e0e0e]">{f.subject}</span>}
                </div>
                <div className="text-[10px] mono text-[#888] whitespace-nowrap">{new Date(f.created_at).toLocaleString()}</div>
              </div>
              <p className="text-sm text-[#333] whitespace-pre-wrap leading-relaxed mb-3">{f.message}</p>
              <div className="flex items-center justify-between gap-3 flex-wrap text-[11px] mono text-[#666]">
                <div className="flex items-center gap-3 flex-wrap">
                  <span>
                    From:{" "}{f.user_name ? <strong className="text-[#0e0e0e]">{f.user_name}</strong> : <em>anon</em>}
                    {f.email && (<>{" · "}<a href={`mailto:${f.email}`} className="text-[#ff4500] underline">{f.email}</a></>)}
                  </span>
                  {f.page_url && (
                    <a href={f.page_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#666] hover:text-[#ff4500]">
                      <ExternalLink className="w-3 h-3" strokeWidth={2} /> {f.page_url}
                    </a>
                  )}
                </div>
                {f.status === "open" ? (
                  <div className="flex items-center gap-2">
                    <button onClick={() => mark(f.feedback_id, "actioned")} disabled={busyId === f.feedback_id}
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#059669] hover:bg-[#047857] disabled:opacity-40 text-white text-[10px] mono uppercase tracking-wider font-bold"
                      data-testid={`feedback-action-${f.feedback_id}`}>
                      <Check className="w-3 h-3" /> Actioned
                    </button>
                    <button onClick={() => mark(f.feedback_id, "dismissed")} disabled={busyId === f.feedback_id}
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#f5f5f5] hover:bg-[#e5e5e5] disabled:opacity-40 text-[#333] text-[10px] mono uppercase tracking-wider font-bold"
                      data-testid={`feedback-dismiss-${f.feedback_id}`}>
                      <EyeOff className="w-3 h-3" /> Dismiss
                    </button>
                  </div>
                ) : (
                  <span className={`px-2 py-0.5 rounded-full text-[10px] mono uppercase tracking-wider font-bold text-white ${f.status === "actioned" ? "bg-[#059669]" : "bg-[#888]"}`}>{f.status}</span>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
